import { useCallback } from 'react'
import { decode } from 'html-entities'

import type { IPropertyCard } from '@awazevr/design-system.compositions.property-card'
import type { Image } from '@awazevr/design-system.compositions.property-list/dist/property-list.types'
import { getBrandConfig } from '@awazevr/mfe-common.configs.brand'
import { useIntlContext } from '@awazevr/mfe-common.contexts.intl'
import type {
  AccommodationType,
  CommonSite,
  SearchCriteria,
} from '@awazevr/mfe-common.types-and-mocks.site-search-service'

import {
  DEFAULT_IMAGE_URL,
  IMAGE_WIDTH_AWAZE,
  IMAGE_WIDTH_NOVASOL,
  PRICE_FROM_NIGHTS,
} from '@/constants'
import {
  formatUrlsForImageArray,
  removeFloorplanImagesFromImageArray,
} from '@/containers/SearchResults/helpers'
import {
  getFormattedDate,
  getParsedNumber,
  setPropertyDetailLink,
} from '@/containers/SearchResults/propertyFormatter.util'
import { useApplicationConfig } from '@/lib/applicationConfig'
import { isNovasolPoweredBrand } from '@/utils/isNovasolPoweredBrand'

/**
 * `PropertyCard` interpolates the card `code` into a Swiper CSS selector for its
 * image carousel (`.${code}-next`). Expanded-polygon property codes are numeric,
 * and a CSS class selector cannot start with a digit, so a bare numeric code
 * throws `'.23653-next' is not a valid selector` at Swiper init. Prefix the code
 * used by the card so the generated selector is always a valid identifier. The
 * real property code is still used for the PDP link (see `pageURL`), so
 * navigation is unaffected.
 * TODO(AS-2729): fix at source in the design-system `PropertyCard` by sanitising
 * its carousel control id (e.g. `CSS.escape` / a non-numeric prefix).
 */
export const toCarouselSafeCode = (code: string): string => `pn-${code}`

// Reuses the results-list image/PDP-link helpers so carousel links stay
// consistent with the main list.
export const usePropertyCardMapper = (
  searchCriteria: SearchCriteria,
): ((properties: CommonSite[]) => IPropertyCard[]) => {
  const { brand } = useApplicationConfig()
  const { formatCurrencyToObject, formatDate, formatMessage } = useIntlContext()

  const { pdpUrlSegment } = getBrandConfig(brand)
  const accommodationType = (searchCriteria.accommodationType ??
    'cottages') as AccommodationType
  const pdpUrlSegmentTranslationKey =
    pdpUrlSegment[accommodationType] || pdpUrlSegment['default']

  const propertyImageWidth = isNovasolPoweredBrand(brand)
    ? IMAGE_WIDTH_NOVASOL
    : IMAGE_WIDTH_AWAZE

  const isDatedSearch = Boolean(searchCriteria.start)

  const dateFormatter = useCallback(
    (isoDate: string): string => {
      const utcDate = new Date(`${isoDate}T00:00:00`)
      return `${formatDate(utcDate, { day: 'numeric' })} ${formatDate(utcDate, {
        month: 'short',
      })}`
    },
    [formatDate],
  )

  return useCallback(
    (properties: CommonSite[]): IPropertyCard[] => {
      const translations: IPropertyCard['translations'] = {
        urgencyBadge: formatMessage({
          id: 'search-results.property-list.urgencyMessage',
          defaultMessage: "Book now before it's gone!",
        }),
        priceUnavailableCTALabel: formatMessage({
          id: 'search-results.property-list.unavailable-price',
          defaultMessage: 'For price, add a start date',
        }),
        nights: formatMessage({
          id: 'search-results.property-list.nights',
          defaultMessage: 'nights',
        }),
        from: formatMessage({
          id: 'search-results.property-list.from',
          defaultMessage: 'From',
        }),
        calloutBadge: formatMessage({
          id: 'search-results.property-list.callout-badge',
          defaultMessage: "Customers' Choice",
        }),
        reviewRating: formatMessage({
          id: 'search-results.property-list.rating',
          defaultMessage: 'Feefo rating',
        }),
        outOf: formatMessage({
          id: 'search-results.property-list.out-of',
          defaultMessage: 'out of',
        }),
        stars: formatMessage({
          id: 'search-results.property-list.stars',
          defaultMessage: 'stars',
        }),
        linkTo: formatMessage({
          id: 'search-results.property-list.link-to',
          defaultMessage: 'Link to',
        }),
        propertyLinkLabel: formatMessage({
          id: 'search-results.property-list.view',
          defaultMessage: 'View',
        }),
        propertyLinkAriaLabel: formatMessage({
          id: 'search-results.property-list.link-aria-label',
          defaultMessage: 'view property (opens in a new window)',
        }),
        includedExtrasLabel: formatMessage({
          id: 'search-results.property-list.includedExtras',
          defaultMessage: 'Price includes',
        }),
        formatCurrency: formatCurrencyToObject,
        dateFormatter,
      }

      const baseParams = new URLSearchParams()
      Object.entries(searchCriteria).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') {
          return
        }
        baseParams.set(
          key,
          Array.isArray(value) ? value.join(',') : String(value),
        )
      })
      baseParams.set('newflex', 'true')

      const pdpUrlStartDate =
        isDatedSearch && searchCriteria.start
          ? getFormattedDate(searchCriteria.start, true)
          : null

      // Drop items with no code: the PDP link would slug to a "…-undefined"
      // 404 and the card would click through to a dead page.
      const validProperties = properties.filter((property) =>
        Boolean(property.code),
      )

      return validProperties.map((property) => {
        const imagesWithoutFloorplans = property.images
          ? removeFloorplanImagesFromImageArray(property.images)
          : []

        const images: Image[] =
          imagesWithoutFloorplans.length === 0
            ? [
                {
                  imageURL: DEFAULT_IMAGE_URL,
                  imgAlt: `${decode(property.displayName ?? '')} images coming soon`,
                },
              ]
            : formatUrlsForImageArray(
                imagesWithoutFloorplans,
                propertyImageWidth,
                brand,
              )

        const lengthOfStay = getParsedNumber(property.lengthOfStay)

        const pageURL = setPropertyDetailLink({
          startDate: pdpUrlStartDate,
          lengthOfStay,
          title: decode(property.displayName ?? ''),
          code: property.code,
          siteName: '',
          siteCode: '',
          pdpUrlSegment: formatMessage({ id: pdpUrlSegmentTranslationKey }),
          searchParameters: new URLSearchParams(baseParams.toString()),
        })

        return {
          // Only the first image: PropertyCard renders its internal Swiper image
          // gallery when images.length > 1, and that Swiper's set-wrapper-size
          // blows the <img> width out to millions of px in the scroller context.
          // A single image renders a static <img> with no Swiper.
          images: images.slice(0, 1),
          pageURL,
          // Force the compact vertical card. PropertyCard renders the wide
          // horizontal (list) layout when isResponsive is true and the window is
          // wide; the carousel needs the narrow stacked card.
          isResponsive: false,
          nights: lengthOfStay ?? PRICE_FROM_NIGHTS,
          hasPetsFree: property.petsGoFree ?? false,
          hasCallout: property.isCustomerChoice ?? false,
          USPs: property.USPs ?? [],
          title: decode(property.displayName ?? ''),
          location: property.location ?? '',
          rhs3: property.rhs3 ?? '',
          startDate: property.startDate ?? '',
          code: toCarouselSafeCode(property.code),
          priceFrom: getParsedNumber(property.priceFrom),
          price: getParsedNumber(property.price),
          wasPrice: getParsedNumber(property.wasPrice),
          grade: getParsedNumber(property.grade) ?? undefined,
          guests: getParsedNumber(property.property?.sleeps) ?? 0,
          bedrooms: getParsedNumber(property.property?.bedrooms) ?? 0,
          bathrooms: getParsedNumber(property.property?.bathrooms) ?? 0,
          pets: getParsedNumber(property.property?.pets) ?? 0,
          score: getParsedNumber(property.averageReviewScore) ?? 0,
          features: [],
          cardinality: property.cardinality,
          translations,
        }
      })
    },
    [
      brand,
      dateFormatter,
      formatCurrencyToObject,
      formatMessage,
      isDatedSearch,
      pdpUrlSegmentTranslationKey,
      propertyImageWidth,
      searchCriteria,
    ],
  )
}
